import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../../abstract/api/rest";
import { StatusTypes } from "../../../abstract/controller/enum/dnd/StatusTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import { isNullish } from "../../../abstract/controller/utils/systemUtils";
import useRequestContainer from "../../../abstract/hook/useRequestContainer";
import useStatus from "../../../abstract/hook/useStatus";
import { getEndpointAI } from "../../../abstract/redux/slice/endpointSlice";
import { getIsExtended, getIsFolded, setMetaFolded } from "../../../abstract/redux/slice/metaSlice";
import { getWindowLock } from "../../../abstract/redux/slice/overlaySlice";
import {
  handleParameterRemove,
  handleParameterRename,
  handleParameterUpdateOutput,
} from "../../../abstract/redux/slice/structureSlice";
import IconChain from "../../../themes/icons/IconChain";
import IconLock from "../../../themes/icons/IconLock";
import Labels from "../../label/Labels";
import Meta from "../../meta/Meta";
import TooltipNode from "../../tooltip/TooltipNode";
import DefaultTooltip, * as vendorTooltip from "../../ui/tooltips/DefaultTooltip";
import styles from "./ParameterHeader.module.scss";

const _inputExists = "↳ Name is not unique";
const _inputEmpty = "↳ Name is not defined";
const _inputColonStart = "↳ Name cannot start with ':'";
const _inputColonEnd = "↳ Name cannot end with ':'";
const _inputColonDouble = "↳ Name cannot contain '::'";

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault = "Parameter name";

const ParameterHeaderContext = createContext();

function ParameterHeader({ id, node, isLatest }) {
  const _status = useStatus(id);

  const _uuid = nodeUtils.getUUID(node);
  const _isFolded = useSelector(getIsFolded(_uuid));
  const _isExtended = useSelector(getIsExtended(_uuid));

  const _name = nodeUtils.getName(node) ?? "";
  const _label = nodeUtils.getLabel(node) ?? [];
  const _defaultValue = nodeUtils.getValueDefault(node) ?? "";

  const [_labelError, _setLabelError] = useState("");
  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);

  //---------------------------------------------------------------------

  const getClassForm = () => {
    return `${styles["header__form"]} ${_isFolded ? styles["header__form--folded"] : ""}`;
  };

  const getClassHeader = () => {
    return `${styles["header"]} ${_isExtended ? styles["header--selected"] : ""} ${
      styles["color--default"]
    } ${!_isFolded && isNullish(_defaultValue) && _name ? styles["style--underline"] : ""}`;
  };

  //---------------------------------------------------------------------

  return (
    <ParameterHeaderContext.Provider
      value={{
        name: _name,
        id: id,
        uuid: _uuid,
        label: _label,
        status: _status,
        node,
        placeholder: _placeholder,
        setPlaceholder: _setPlaceholder,
        labelError: _labelError,
        setLabelError: _setLabelError,
        isFolded: _isFolded,
        isExtended: _isExtended,
        isLatest,
      }}>
      <>
        <div id={`head-${_uuid}`} className={getClassHeader()}>
          <form
            id={`head-form-${_uuid}`}
            className={getClassForm()}
            onSubmit={(e) => e.preventDefault()}>
            <HeaderInputName />
            <TypeLinked />
            <TypeReferenced />
            {!_isFolded && _label.length > 0 && <Labels node={node} />}
            <Meta node={node} status={_status} show={_isExtended} />
            <TypeDefault />
          </form>
        </div>
      </>
    </ParameterHeaderContext.Provider>
  );
}

function HeaderInputName() {
  const _dispatch = useDispatch();
  const {
    id,
    name,
    uuid,
    node,
    isFolded,
    isExtended,
    status,
    labelError,
    setLabelError,
    isHidden,
    placeholder,
    setPlaceholder,
  } = useContext(ParameterHeaderContext);

  const _endpointAI = useSelector(getEndpointAI);

  const _idTarget = nodeUtils.getIdTarget(node);
  const _nameTarget = _idTarget ? pathUtils.getName(_idTarget) : null;

  const _container = useRequestContainer({ id, linkedName: _nameTarget, type: "parameter" });

  const [_inputName, _setInputName] = useState(name);
  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const _nameRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);
  const _abortRef = useRef(null);

  const _children = nodeUtils.getParameters(_container.nodeParent);
  const _childrenNames = _children.map((e) => nodeUtils.getName(e));

  const getClassName = () => {
    return `${styles["header__name-input"]} ${
      isExtended ? styles["color--selected"] : styles["color--default"]
    } ${labelError ? styles["style--stroke"] : ""}`;
  };

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionUpdateNameAccept = useCallback(() => {
    clearSuggestionTimeout();

    if (!labelError && _inputName) {
      _dispatch(handleParameterRename(id, _inputName));
    }
  }, [clearSuggestionTimeout, labelError, _inputName, _dispatch, id]);

  const actionUpdateNameCancel = useCallback(() => {
    clearSuggestionTimeout();

    if (name) {
      _setInputName(name);
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      _dispatch(handleParameterRemove(id));
    }
  }, [_dispatch, id, name, clearSuggestionTimeout, setLabelError, setPlaceholder]);

  const stopSuggestionLoading = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
      _suggestionTimeoutRef.current = null;
      _setIsLoadingSuggestion(false);
    }
  }, []);

  const handleInputNameOnNameChange = (e) => {
    const _value = e.target.value;

    _setInputName(_value);

    if (_value && _value.trim() !== "") {
      stopSuggestionLoading();
    }

    if (_value.startsWith(":")) {
      setLabelError(_inputColonStart);
    } else if (_value.endsWith(":")) {
      setLabelError(_inputColonEnd);
    } else if (_value.includes("::")) {
      setLabelError(_inputColonDouble);
    } else if (_childrenNames.includes(_value) && name !== _value) {
      setLabelError(_inputExists);
    } else if (!_value) {
      setLabelError(_inputEmpty);
    } else {
      setLabelError("");
    }

    setPlaceholder(_placeholderDefault);

    if (labelError === "") {
      if (_value && _value.trim() !== "") {
        if (_nameRef.current.selectionEnd >= _nameRef.current.textLength) {
          suggestNameSuffix(_value);
        } else {
          clearSuggestionTimeout();
        }
      }
    }
  };

  const handleInputNameFocusIn = async () => {
    if (isFolded) {
      _nameRef.current.blur();
      return;
    }

    _dispatch(setMetaFolded(uuid, false));

    if (!name && (!_inputName || _inputName.trim() === "")) {
      suggestName();
    } else {
      _nameRef.current.select();
    }
  };

  const handleInputNameFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    clearSuggestionTimeout();

    if (labelError) {
      if (name) {
        _setInputName(name);
        _dispatch(handleParameterRename(id, name));
      } else {
        _dispatch(handleParameterRemove(id));
      }
    } else {
      if (_inputName) {
        _dispatch(handleParameterRename(id, _inputName));
      } else {
        if (name) {
          _dispatch(handleParameterRename(id, name));
        } else {
          _dispatch(handleParameterRemove(id));
        }
      }
    }

    setLabelError("");
    setPlaceholder(_placeholderDefault);
  };

  const suggestName = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }

    _setIsLoadingSuggestion(true);
    setPlaceholder(_placeholderSuggestion);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_nameRef.current) {
        if (_suggestion.status !== "error" && !_nameRef.current.value) {
          _setInputName(_suggestion.message);

          _nameRef.current.value = _suggestion.message;
          _nameRef.current.select();
        } else {
          setPlaceholder("Suggestion error");
        }
      }
    } catch (error) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout, setPlaceholder]);

  const suggestNameSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }

      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setInputName(_suggestion.message);

            if (_nameRef.current) {
              _nameRef.current.value = _suggestion.message;
              _nameRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          }
        } catch (error) {}

        clearSuggestionTimeout();
      }, 1000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    const _target = _nameRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        if (!_isLoadingSuggestion) {
          actionUpdateNameAccept();
        }
      } else if (e.code === "Escape") {
        actionUpdateNameCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestNameSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    if (_target) {
      _target.addEventListener("keydown", callback);
    }

    return () => (_target ? _target.removeEventListener("keydown", callback) : {});
  }, [
    _isLoadingSuggestion,
    actionUpdateNameAccept,
    actionUpdateNameCancel,
    clearSuggestionTimeout,
    suggestNameSuffix,
  ]);

  useEffect(() => {
    if (status === StatusTypes.DRAGGED) {
      _nameRef.current.blur();
    }
  }, [status]);

  useEffect(() => {
    if (!_nameRef.current) {
      return;
    }

    if (!name) {
      _nameRef.current.focus();
    } else {
      _nameRef.current.blur();
    }
  }, [name]);

  useEffect(() => {
    return () => {
      if (_suggestionTimeoutRef.current) {
        clearTimeout(_suggestionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {!isFolded ? (
        <>
          <input
            id={`head-form-input-name-${uuid}`}
            data-tooltip-id={`node-${uuid}`}
            data-tooltip-delay-show={vendorTooltip.getTimeFast()}
            ref={_nameRef}
            type="text"
            className={`${getClassName()} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
            value={_inputName}
            placeholder={placeholder}
            onChange={handleInputNameOnNameChange}
            onFocus={handleInputNameFocusIn}
            onBlur={handleInputNameFocusOut}
          />
          <TooltipNode node={node} handle={`node-${uuid}`} isHidden={isHidden} />
        </>
      ) : (
        <ViewFolded />
      )}
      <label className={styles["error"]}>{labelError}</label>
    </>
  );
}

function ViewFolded() {
  const { node } = useContext(ParameterHeaderContext);

  const _name = nodeUtils.getName(node);

  return (
    <div className={`${styles["fold-container"]}`}>
      <span className={styles["fold-full"]}>{_name}</span>
    </div>
  );
}

function TypeLinked() {
  const { uuid, node, status, isFolded } = useContext(ParameterHeaderContext);
  const _windowLock = useSelector(getWindowLock);

  const _idTarget = nodeUtils.getIdTarget(node);
  const _nameTarget = _idTarget ? pathUtils.getName(_idTarget) : "";

  if (!_idTarget || isFolded) {
    return null;
  }

  return (
    <div className={styles["header__special"]}>
      <div
        className={styles["header__special-flex"]}
        data-tooltip-id={`parameter-lock-${uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
        <IconLock /> {pathUtils.getSeparatorGUI1()}
        <div className={styles["header__special-lock"]}>{_nameTarget}</div>
      </div>
      {status !== StatusTypes.DRAGGED && !isFolded && !_windowLock && _nameTarget.length > 15 && (
        <DefaultTooltip handle={`parameter-lock-${uuid}`}>{_nameTarget}</DefaultTooltip>
      )}
    </div>
  );
}

function TypeReferenced() {
  const { uuid, node, isFolded } = useContext(ParameterHeaderContext);
  const _windowLock = useSelector(getWindowLock);

  const _idTarget = nodeUtils.getIdTarget(node);
  const _references = nodeUtils.getIdReferences(node);

  if (_references.length === 0 || isFolded || _idTarget) {
    return null;
  }

  const _refDiv = (
    <div>
      {_references.map((x, i) => (
        <div key={i}>{pathUtils.getNameSimplified(x)}</div>
      ))}
    </div>
  );

  return (
    <div className={styles["header__special"]}>
      <div
        className={styles["header__special-flex"]}
        data-tooltip-id={`parameter-chain-${uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
        <IconChain /> {pathUtils.getSeparatorGUI1()}
        <div className={styles["header__special-lock"]}>Number of links: {_references.length}</div>
      </div>
      {!_windowLock && (
        <DefaultTooltip handle={`parameter-chain-${uuid}`}>{_refDiv}</DefaultTooltip>
      )}
    </div>
  );
}

function TypeDefault() {
  const _dispatch = useDispatch();
  const { id, node, isFolded } = useContext(ParameterHeaderContext);

  const _defaultValue = nodeUtils.getValueDefault(node);
  const [_outputValue, _setOutputValue] = useState(_defaultValue);

  const _refDefaultValue = useRef(null);

  const _name = nodeUtils.getName(node);

  const actionUpdateDefaultAccept = useCallback(() => {
    _dispatch(handleParameterUpdateOutput(id, _outputValue));
  }, [id, _outputValue, _dispatch]);

  const actionUpdateDefaultCancel = useCallback(() => {
    _refDefaultValue.current.blur();
  }, []);

  const handleInputDefaultOnChange = (e) => {
    _setOutputValue(e.target.value);
  };

  const handleInputDefaultFocusIn = () => {
    _refDefaultValue.current.select();
  };

  const handleInputDefaultFocusOut = () => {
    actionUpdateDefaultAccept();
  };

  useEffect(() => {
    const callback = (e) => {
      if (e.code === "Enter") {
        actionUpdateDefaultAccept();
      } else if (e.code === "Escape") {
        actionUpdateDefaultCancel();
      }
    };

    const _target = _refDefaultValue.current;

    if (_target) {
      _target.addEventListener("keydown", callback);
    }

    return () => (_target ? _target.removeEventListener("keydown", callback) : {});
  }, [actionUpdateDefaultAccept, actionUpdateDefaultCancel]);

  useEffect(() => {
    if (_name && !_outputValue && !isNullish(_defaultValue) && !isFolded) {
      _refDefaultValue.current.focus();
    }
  }, [_name, _outputValue, _defaultValue, isFolded]);

  if (isNullish(_defaultValue) || isFolded) {
    return null;
  }

  return (
    <div className={styles["header__special"]}>
      <div className={styles["header__special-flex-column"]}>
        <div className={styles["header__default-text"]}>Default value</div>
        <input
          ref={_refDefaultValue}
          type="text"
          className={`${styles["header__default"]}`}
          value={_outputValue}
          placeholder={"Empty"}
          onChange={handleInputDefaultOnChange}
          onFocus={handleInputDefaultFocusIn}
          onBlur={handleInputDefaultFocusOut}
        />
      </div>
    </div>
  );
}

//--------------------------------------------------

export default ParameterHeader;
